<template>
    <div class="automation-action-settings">
        <div class="field">
            <label for="notification_schedule">{{ t('automations-form-actions-notification-schedule_label') }}</label>
            <small v-html="t('automations-form-actions-notification-schedule_help')">
            </small>
            <select id="notification_schedule" type="tel" v-model="notification_schedule" class="form-control">
                <option value="from_place">
                    {{ t('automations-form-actions-notification-schedule_options-from_place') }}
                </option>
                <option value="anytime">{{ t('automations-form-actions-notification-schedule_options-anytime') }}</option>
            </select>
        </div>
        <div class="field">
            <label for="timeout">{{ t('automations-form-actions-notification-timeout_label') }}</label>
            <small>
                {{ t('automations-form-actions-notification-timeout_help') }}
            </small>
            <input id="timeout" type="tel" v-model="timeout" class="form-control" :placeholder="t('automations-form-actions-notification-timeout_placeholder')" />
        </div>
        <div class="field">
            <label for="sendMethod">{{ t('automations-form-actions-notification-method_label') }}</label>
            <small>
                {{ t('automations-form-actions-notification-method_help') }}
            </small>
            <select id="sendMethod" class="form-control" v-model="sendMethod">
                <option disabled :value="null">{{ t('automations-form-actions-notification-method_placeholder') }}</option>
                <option value="sms" v-if="$module('sms')">{{ t('sms') }}</option>
                <option value="rcs" v-if="$module('rcs')">{{ t('rcs') }}</option>
                <option value="email" v-if="$module('email')">{{ t('email') }}</option>
                <option value="whatsapp" v-if="$module('whatsapp')">
                    {{ t('whatsapp') }}
                </option>
                <option
                    value="whatsapp-cloud"
                    v-if="$module('whatsapp') && wppCloudSenders.length > 0"
                >
                    <!-- i18n-validator-ignore-next-line: WhatsApp Cloud API is a proper technical term -->
                    {{ t('whatsapp') }} Cloud
                </option>
            </select>
        </div>
        <div class="field">
            <label for="recipient_mode">{{ t('automations-form-actions-notification-recipient_label') }}</label>
            <small>
                {{ t('automations-form-actions-notification-recipient_help') }}
            </small>
            <select id="recipient_mode" class="form-control" v-model="recipient_mode">
                <option value="client">{{ recipientClientLabel }}</option>
                <option value="custom">{{ recipientCustomLabel }}</option>
            </select>
        </div>
        <div class="field" v-if="recipient_mode === 'custom'">
            <label for="recipient_address">{{ recipientAddressLabel }}</label>
            <small>
                {{ t('automations-form-actions-notification-recipient_address_help') }}
            </small>
            <input
                id="recipient_address"
                type="text"
                class="form-control"
                v-model="recipient_address"
                :placeholder="recipientAddressPlaceholder"
            />
            <div v-if="recipientAddressError" class="invalid-feedback">
                {{ recipientAddressError }}
            </div>
            <small>
                <ul>
                    <li class="token" v-for="(token, t) in tokens" :key="t">
                        <span class="copy-token">{{ token }}</span>
                        <a @click="copyToken(token)" class="copy-btn"><i class="fa fa-copy"></i></a>
                    </li>
                </ul>
            </small>
        </div>
        <div class="field" v-show="sendMethod !== 'whatsapp' && (getSenders(sendMethod).length > 0 || sendMethod === 'rcs')">
            <label for="place_sender">{{ t('automations-form-actions-notification-sender_label') }}</label>
            <select id="place_sender" v-model="places_senders_id" class="form-control">
                <option :value="null">
                    {{ defaultSenderOptionLabel(sendMethod) }}
                </option>
                <option
                    v-for="sender in getSenders(sendMethod)"
                    :value="String(sender.id)"
                    :key="sender.id"
                >
                    {{ sender.alias }}
                </option>
            </select>
            <div
                v-if="!places_senders_id && !hasDefaultSender(sendMethod)"
                class="alert alert-warning default-sender-alert"
            >
                {{ t('automations-form-actions-notification-no_default_sender_warning') }}<a :href="connectionsUrl(sendMethod)" target="_blank" class="default-sender-link">{{ t('automations-form-actions-notification-configure_default_sender_link') }}</a>.
            </div>
        </div>
        <template v-if="sendMethod === 'whatsapp'">
            <div class="field whatsapp-sender-selection">
                <label>{{ t('automations-form-actions-notification-sender_label') }}</label>
                <small>
                    {{ t('automations-form-actions-notification-whatsapp-selection_help') }}
                </small>

                <div class="whatsapp-sender-option">
                    <label class="radio-label">
                        <input type="radio" v-model="selectNumber" value="manually" />
                        <span>{{ t('automations-form-actions-notification-whatsapp-select_manually') }}</span>
                    </label>
                </div>
                <div v-if="selectNumber === 'manually'" class="whatsapp-sender-option">
                    <select class="form-control" v-model="places_senders_id">
                        <option :value="null">{{ defaultSenderOptionLabel('whatsapp') }}</option>
                        <option
                            v-for="sender in getSenders('whatsapp')"
                            :value="String(sender.id)"
                            :key="sender.id"
                        >
                            {{ sender.alias }}
                        </option>
                    </select>
                    <div
                        v-if="!places_senders_id && !hasDefaultSender('whatsapp')"
                        class="alert alert-warning default-sender-alert"
                    >
                        {{ t('automations-form-actions-notification-no_default_sender_warning') }}<a :href="connectionsUrl('whatsapp')" target="_blank" class="default-sender-link">{{ t('automations-form-actions-notification-configure_default_sender_link') }}</a>.
                    </div>
                </div>

                <div
                    v-if="$module('client_fields') && userCustomFields.length > 0"
                    class="whatsapp-sender-option"
                >
                    <label class="radio-label">
                        <input type="radio" v-model="selectNumber" value="by_custom_field" />
                        <span>{{ t('automations-form-actions-notification-whatsapp-client_field') }}</span>
                    </label>
                </div>
                <div v-if="selectNumber === 'by_custom_field'" class="whatsapp-sender-option">
                    <select class="form-control" v-model="senderCustomField">
                        <option disabled :value="null">{{ t('automations-form-actions-notification-whatsapp-select_client_field') }}</option>
                        <option
                            v-for="field in userCustomFields"
                            :value="field.id"
                            :key="field.id"
                        >
                            {{ field.field_name }}
                        </option>
                    </select>
                    <small class="text-muted d-block mt-1">
                        {{ t('automations-form-actions-notification-whatsapp-custom_field_fallback_help') }}
                    </small>
                    <div
                        v-if="!hasDefaultSender('whatsapp')"
                        class="alert alert-warning default-sender-alert"
                    >
                        {{ t('automations-form-actions-notification-no_default_sender_warning') }}<a :href="connectionsUrl('whatsapp')" target="_blank" class="default-sender-link">{{ t('automations-form-actions-notification-configure_default_sender_link') }}</a>.
                    </div>
                </div>

                <div class="whatsapp-sender-option">
                    <label class="radio-label">
                        <input type="radio" v-model="selectNumber" value="by_last_branch" />
                        <span>{{ t('automations-form-actions-notification-whatsapp-last_branch') }}</span>
                    </label>
                    <small v-if="selectNumber === 'by_last_branch'" class="text-muted d-block mt-1">
                        {{ t('automations-form-actions-notification-whatsapp-last_branch_fallback_help') }}
                    </small>
                    <div
                        v-if="selectNumber === 'by_last_branch' && !hasDefaultSender('whatsapp')"
                        class="alert alert-warning default-sender-alert"
                    >
                        {{ t('automations-form-actions-notification-no_default_sender_warning') }}<a :href="connectionsUrl('whatsapp')" target="_blank" class="default-sender-link">{{ t('automations-form-actions-notification-configure_default_sender_link') }}</a>.
                    </div>
                </div>
            </div>
        </template>
        <div v-if="sendMethod === 'whatsapp-cloud'">
            <div class="field" v-if="places_senders_id">
                <select
                    class="form-control"
                    v-model="whatsappCloudOptions.template_id"
                >
                    <option disabled :value="null">{{ t('automations-form-actions-notification-template_placeholder') }}</option>
                    <option
                        v-for="template in templates[places_senders_id]"
                        :value="template.id"
                        :key="template.id"
                    >
                        {{ template.name }} - ({{ statusTranslate(template.status) }})
                    </option>
                </select>
            </div>
            <div class="field wpp-cloud-components" v-if="activeTemplate">
                <div
                    v-for="(component, index) in activeTemplate.components"
                    :key="index"
                >
                    <div
                        v-if="component.type === 'HEADER'"
                        :key="component.type"
                    >
                        <div>
                            <strong>{{ t('automations-form-actions-notification-wpp_cloud-header') }}</strong>
                        </div>
                        <div v-if="component.format === 'TEXT'" class="text-example">
                            <strong>{{ component.text }}</strong>
                        </div>
                        <div
                            v-if="
                                component.example &&
                                component.example['header_handle'] &&
                                component.example['header_handle'].length > 0 &&
                                wppCloudMediaHeaderFormats.includes(component.format)
                            "
                        >
                            <div
                                v-for="(variable, idx) in component.example['header_handle']"
                                :key="idx"
                            >
                                <div v-if="['IMAGE', 'GIF'].includes(component.format)" class="image-preview-container">
                                    <strong>{{ wppCloudHeaderExamplePreviewLabel(component.format) }}</strong>
                                    <div class="image-preview">
                                        <img
                                            :src="variable"
                                            :alt="`Header handle ${idx + 1}`"
                                            class="preview-image default-preview"
                                            @error="handleImageError($event, 'default')"
                                        >
                                    </div>
                                </div>
                                <div v-else-if="component.format === 'VIDEO'" class="image-preview-container">
                                    <strong>{{ wppCloudHeaderExamplePreviewLabel(component.format) }}</strong>
                                    <div class="image-preview">
                                        <video
                                            :src="variable"
                                            class="preview-image default-preview"
                                            controls
                                            preload="metadata"
                                            style="max-width: 100%; max-height: 200px;"
                                        >
                                            {{ t('automations-form-actions-notification-wpp_cloud-video_not_supported') }}
                                        </video>
                                    </div>
                                </div>
                                <div v-else-if="component.format === 'DOCUMENT'" class="image-preview-container">
                                    <strong>{{ wppCloudHeaderExamplePreviewLabel(component.format) }}</strong>
                                    <p class="text-muted small mb-0">
                                        {{ t('automations-form-actions-notification-wpp_cloud-header_document_example_hint') }}
                                    </p>
                                </div>

                                <div class="url-input-section">
                                    <strong>{{ wppCloudHeaderMediaUrlLabel(component.format, idx + 1) }}</strong>
                                    <input
                                        type="text"
                                        class="form-control"
                                        :placeholder="variable"
                                        v-model="whatsappCloudOptions.params.header_handle[idx]"
                                        @input="validateMediaUrl(whatsappCloudOptions.params.header_handle[idx], `header_handle_${idx}`, component.format)"
                                    />

                                    <div class="mt-2">
                                        <a href="/#/files-gallery" target="_blank" class="btn btn-sm btn-outline-primary">
                                            <i class="fa fa-upload"></i> {{ t('automations-form-actions-notification-wpp_cloud-upload_button') }}
                                        </a>
                                    </div>

                                    <div v-if="imageValidation[`header_handle_${idx}`] && imageValidation[`header_handle_${idx}`].isLoading" class="text-info mt-2">
                                        <i class="fa fa-spinner fa-spin"></i> {{ t('automations-form-actions-notification-wpp_cloud-validating_media') }}
                                    </div>

                                    <div v-if="imageValidation[`header_handle_${idx}`] && imageValidation[`header_handle_${idx}`].error && !imageValidation[`header_handle_${idx}`].isSmartTag" class="text-danger mt-2">
                                        <i class="fa fa-exclamation-triangle"></i> {{ imageValidation[`header_handle_${idx}`].error }}
                                    </div>

                                    <div v-if="imageValidation[`header_handle_${idx}`] && imageValidation[`header_handle_${idx}`].isSmartTag && imageValidation[`header_handle_${idx}`].isValid" class="text-success mt-2">
                                        <i class="fa fa-check-circle"></i> {{ imageValidation[`header_handle_${idx}`].error }}
                                    </div>

                                    <div v-if="imageValidation[`header_handle_${idx}`] && imageValidation[`header_handle_${idx}`].isSmartTag && !imageValidation[`header_handle_${idx}`].isValid" class="text-danger mt-2">
                                        <i class="fa fa-exclamation-triangle"></i> {{ imageValidation[`header_handle_${idx}`].error }}
                                    </div>

                                    <div
                                        v-if="whatsappCloudOptions.params.header_handle[idx] &&
                                              whatsappCloudOptions.params.header_handle[idx] !== variable &&
                                              imageValidation[`header_handle_${idx}`] &&
                                              imageValidation[`header_handle_${idx}`].isValid &&
                                              !imageValidation[`header_handle_${idx}`].isSmartTag"
                                        class="custom-media-preview mt-3"
                                    >
                                        <div class="media-preview-container">
                                            <strong>{{ t('automations-form-actions-notification-wpp_cloud-custom_media_preview', [wppCloudCustomMediaTypeLabel(imageValidation[`header_handle_${idx}`].mediaType)]) }}</strong>
                                            <div class="media-preview">
                                                <img
                                                    v-if="imageValidation[`header_handle_${idx}`].mediaType === 'image'"
                                                    :src="whatsappCloudOptions.params.header_handle[idx]"
                                                    :alt="`Custom image ${idx + 1}`"
                                                    class="preview-media custom-preview"
                                                />
                                                <video
                                                    v-else-if="imageValidation[`header_handle_${idx}`].mediaType === 'video'"
                                                    :src="whatsappCloudOptions.params.header_handle[idx]"
                                                    class="preview-media custom-preview"
                                                    controls
                                                    preload="metadata"
                                                    style="max-width: 100%; max-height: 200px;"
                                                >
                                                    {{ t('automations-form-actions-notification-wpp_cloud-video_not_supported') }}
                                                </video>
                                                <p
                                                    v-else-if="imageValidation[`header_handle_${idx}`].mediaType === 'document'"
                                                    class="text-muted small mb-0"
                                                >
                                                    {{ t('automations-form-actions-notification-wpp_cloud-document_url_accepted_hint') }}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <small v-if="!whatsappCloudOptions.params.header_handle[idx] || !whatsappCloudOptions.params.header_handle[idx].trim()" class="text-danger mt-2 d-block">
                                        {{ t('automations-form-actions-notification-wpp_cloud-required_error') }}
                                    </small>
                                </div>
                            </div>
                            <div v-if="component.format === 'DOCUMENT'" class="url-input-section mt-2">
                                <strong>{{ t('automations-form-actions-notification-wpp_cloud-document_filename_label') }}</strong>
                                <small class="text-muted d-block mb-1">
                                    {{ t('automations-form-actions-notification-wpp_cloud-document_filename_help') }}
                                </small>
                                <input
                                    type="text"
                                    class="form-control"
                                    v-model="whatsappCloudOptions.params.document_filename"
                                    :placeholder="t('automations-form-actions-notification-wpp_cloud-document_filename_placeholder')"
                                />
                            </div>
                        </div>
                        <div
                            v-if="component.format === 'LOCATION'"
                            class="url-input-section mt-2"
                        >
                            <strong>{{ t('automations-form-actions-notification-wpp_cloud-location_header_label') }}</strong>
                            <div class="row mt-2">
                                <div class="col-md-6 mb-2">
                                    <label class="small text-muted mb-0">{{ t('automations-form-actions-notification-wpp_cloud-location_latitude_label') }}</label>
                                    <input
                                        type="text"
                                        class="form-control"
                                        v-model="whatsappCloudOptions.params.location_latitude"
                                        :placeholder="t('automations-form-actions-notification-wpp_cloud-location_latitude_placeholder')"
                                    />
                                </div>
                                <div class="col-md-6 mb-2">
                                    <label class="small text-muted mb-0">{{ t('automations-form-actions-notification-wpp_cloud-location_longitude_label') }}</label>
                                    <input
                                        type="text"
                                        class="form-control"
                                        v-model="whatsappCloudOptions.params.location_longitude"
                                        :placeholder="t('automations-form-actions-notification-wpp_cloud-location_longitude_placeholder')"
                                    />
                                </div>
                                <div class="col-md-6 mb-2">
                                    <label class="small text-muted mb-0">{{ t('automations-form-actions-notification-wpp_cloud-location_name_label') }}</label>
                                    <input
                                        type="text"
                                        class="form-control"
                                        v-model="whatsappCloudOptions.params.location_name"
                                        :placeholder="t('automations-form-actions-notification-wpp_cloud-location_name_placeholder')"
                                    />
                                </div>
                                <div class="col-md-6 mb-2">
                                    <label class="small text-muted mb-0">{{ t('automations-form-actions-notification-wpp_cloud-location_address_label') }}</label>
                                    <input
                                        type="text"
                                        class="form-control"
                                        v-model="whatsappCloudOptions.params.location_address"
                                        :placeholder="t('automations-form-actions-notification-wpp_cloud-location_address_placeholder')"
                                    />
                                </div>
                            </div>
                            <small
                                v-if="!wppCloudLocationParamsFilled"
                                class="text-danger mt-1 d-block"
                            >
                                {{ t('automations-form-actions-notification-wpp_cloud-required_error') }}
                            </small>
                        </div>
                        <div
                            v-if="
                                component.example &&
                                component.example['header_text'] &&
                                component.example['header_text'].length > 0
                            "
                        >
                            <div
                                v-for="(variable, idx) in component.example[
                                    'header_text'
                                ]"
                                :key="idx"
                            >
                                <strong>{{ idx + 1 }}:</strong>
                                <input
                                    type="text"
                                    class="form-control"
                                    :placeholder="variable"
                                    v-model="whatsappCloudOptions.params.header_text[idx]"
                                />
                                <small v-if="!whatsappCloudOptions.params.header_text[idx] || !whatsappCloudOptions.params.header_text[idx].trim()" class="text-danger">
                                    {{ t('automations-form-actions-notification-wpp_cloud-required_error') }}
                                </small>
                            </div>
                        </div>
                    </div>
                    <div
                        v-if="component.type === 'BUTTONS'"
                        :key="component.type"
                    >
                        <div
                            v-for="(button, idxButton) in component.buttons"
                            :key="'btn-' + idxButton"
                        >
                            <div
                                v-show="(button.type === 'URL' || button.type === 'COPY_CODE') && button.example"
                            >
                                <strong>{{ t('automations-form-actions-notification-wpp_cloud-buttons_label', [button.text]) }}</strong>
                                <div v-if="(button.type === 'URL' || button.type === 'COPY_CODE') && button.example">
                                    <strong>{{ "1" }}:</strong>
                                    <input
                                        type="text"
                                        class="form-control"
                                        :placeholder="button.example[0]"
                                        v-model="whatsappCloudOptions.params.buttons[idxButton]"
                                    />
                                    <small v-if="!whatsappCloudOptions.params.buttons[idxButton] || !whatsappCloudOptions.params.buttons[idxButton].trim()" class="text-danger">
                                        {{ t('automations-form-actions-notification-wpp_cloud-required_error') }}
                                    </small>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        v-if="component.type === 'BODY'"
                        :key="component.type"
                    >
                        <div>
                            <strong>{{ t('automations-form-actions-notification-wpp_cloud-body') }}</strong>
                        </div>
                        <div class="text-example">{{ component.text }}</div>
                        <div
                            v-if="
                                component.example &&
                                component.example['body_text'] &&
                                component.example['body_text'].length > 0 &&
                                component.example['body_text'][0] &&
                                component.example['body_text'][0].length > 0
                            "
                        >
                            <div
                                v-for="(variable, idxBody) in component.example[
                                    'body_text'
                                ][0]"
                                :key="idxBody"
                            >
                                <strong>{{ idxBody + 1 }}:</strong>
                                <input
                                    type="text"
                                    class="form-control"
                                    :placeholder="variable"
                                    v-model="whatsappCloudOptions.params.body_text[idxBody]"
                                />
                                <small v-if="!whatsappCloudOptions.params.body_text[idxBody] || !whatsappCloudOptions.params.body_text[idxBody].trim()" class="text-danger">
                                    {{ t('automations-form-actions-notification-wpp_cloud-required_error') }}
                                </small>
                            </div>
                        </div>
                        <div
                            v-if="
                                component.example &&
                                component.example['body_handle'] &&
                                component.example['body_handle'].length > 0
                            "
                        >
                            <div
                                v-for="(param, idxHandle) in component.example['body_handle']"
                                :key="'body_handle_' + idxHandle"
                            >
                                <strong>{{ param.param_name }}:</strong>
                                <input
                                    type="text"
                                    class="form-control"
                                    :placeholder="param.example"
                                    :value="getBodyHandleValue(param.param_name)"
                                    @input="updateBodyHandle(param.param_name, $event.target.value)"
                                />
                                <small v-if="!getBodyHandleValue(param.param_name) || !getBodyHandleValue(param.param_name).trim()" class="text-danger">
                                    {{ t('automations-form-actions-notification-wpp_cloud-required_error') }}
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="field" v-if="sendMethod === 'email'">
            <label for="subject">{{ t('automations-form-actions-notification-email-subject_label') }}</label>
            <input id="subject" class="form-control" v-model="subject" />
        </div>
        <div class="field">
            <label v-if="sendMethod !== 'email'">{{ t('automations-form-actions-notification-content_label') }}</label>
            <div v-else-if="sendMethod === 'email'">
              <div
                :style="{
                        marginBottom: '20px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }">
                <label>{{ t('automations-form-actions-notification-email-content_label') }}</label>
                <button
                  class="btn btn-primary"
                  style="min-width: 150px"
                  @click.prevent="editingEmailCode = true"
                >
                  {{ t('automations-form-actions-notification-email-edit_html_button') }}
                </button>
                <button
                  class="btn btn-primary"
                  style="min-width: 150px"
                  @click.prevent="openEmailEditor()"
                >
                  {{ t('automations-form-actions-notification-email-edit_content_button') }}
                </button>
              </div>
            </div>

            <template v-if="sendMethod === 'email' && editingEmail">
                <div v-if="resolvedEmailEditor === 'kv'">
                    <EmailVisualEditor
                        v-model="email_doc_draft"
                        :snippets="tokens"
                        @save="onKvEmailSave"
                        @close="cancelEmailEditor"
                    />
                </div>
                <div v-else @keydown.esc.stop.prevent="onGrapesEsc">
                    <grapejs
                        :tokens="tokens"
                        :html="content"
                        @saveEmail="
                            (html) => {
                                this.content = html;
                                editingEmail = false;
                                editingEmailCode = false;
                                this.email_editor = 'grapejs';
                            }
                        "
                        @cancelEmail="cancelEmailEditor()"
                    />
                </div>
            </template>
            <template v-if="editingEmailCode && sendMethod==='email' && !editingEmail">
              <codeEditor :html="content" @saveEmail="(html) => {
                this.content = html;
                editingEmailCode = false;
              }" @cancelEmail="editingEmailCode = false"></codeEditor>
            </template>
            <div v-show="sendMethod === 'email' && !editingEmail && !editingEmailCode" class="iframe-holder">
                <iframe ref="iframe" />
            </div>
            <small>
                {{ t('automations-form-actions-notification-common-snippets_help') }}
            </small>
            <small>
                <ul>
                    <li class="token" v-for="(token, t) in tokens" :key="t">
                        <span class="copy-token">{{ token }}</span>
                        <a @click="copyToken(token)" class="copy-btn"><i class="fa fa-copy"></i></a>
                    </li>
                </ul>
            </small>
            <template v-if="sendMethod === 'whatsapp'">
                <div class="input-group">
                    <textarea class="form-control" v-model="content" :placeholder="t('automations-form-actions-notification-whatsapp-message_placeholder')"></textarea>
                </div>
                <div class="input-group" style="width: 100%;">
                    <label class="mb-2">
                        <input type="checkbox" v-model="sendWhatsappFile" />
                        {{ t('automations-form-actions-notification-whatsapp-send_file_label') }}
                    </label>
                    <section v-show="sendWhatsappFile" style="width: 100%;">
                        <input
                            type="text"
                            class="form-control"
                            v-model="whatsappFile"
                            :placeholder="t('automations-form-actions-notification-whatsapp-file_url_placeholder')"
                            @input="validateMediaUrl(whatsappFile, 'whatsapp_file')"
                        />

                        <div class="mt-2">
                            <a href="/#/files-gallery" target="_blank" class="btn btn-sm btn-outline-primary">
                                <i class="fa fa-upload"></i> {{ t('automations-form-actions-notification-whatsapp-upload_help') }}
                            </a>
                        </div>

                        <div v-if="imageValidation['whatsapp_file'] && imageValidation['whatsapp_file'].isLoading" class="text-info mt-2">
                            <i class="fa fa-spinner fa-spin"></i> {{ t('automations-form-actions-notification-whatsapp-validating_media') }}
                        </div>

                        <div v-if="imageValidation['whatsapp_file'] && imageValidation['whatsapp_file'].error && !imageValidation['whatsapp_file'].isSmartTag" class="text-danger mt-2">
                            <i class="fa fa-exclamation-triangle"></i> {{ imageValidation['whatsapp_file'].error }}
                        </div>

                        <div v-if="imageValidation['whatsapp_file'] && imageValidation['whatsapp_file'].isSmartTag && imageValidation['whatsapp_file'].isValid" class="text-success mt-2">
                            <i class="fa fa-check-circle"></i> {{ imageValidation['whatsapp_file'].error }}
                        </div>

                        <div v-if="imageValidation['whatsapp_file'] && imageValidation['whatsapp_file'].isSmartTag && !imageValidation['whatsapp_file'].isValid" class="text-danger mt-2">
                            <i class="fa fa-exclamation-triangle"></i> {{ imageValidation['whatsapp_file'].error }}
                        </div>

                        <div
                            v-if="whatsappFile &&
                                  whatsappFile.trim() &&
                                  imageValidation['whatsapp_file'] &&
                                  imageValidation['whatsapp_file'].isValid &&
                                  !imageValidation['whatsapp_file'].isSmartTag"
                            class="custom-media-preview mt-3"
                        >
                            <div class="media-preview-container">
                                <strong>{{ t('automations-form-actions-notification-whatsapp-preview_label', [imageValidation['whatsapp_file'].mediaType === 'video' ? t('automations-form-actions-notification-whatsapp-media_types-video') : t('automations-form-actions-notification-whatsapp-media_types-image')]) }}</strong>
                                <div class="media-preview">
                                    <img
                                        v-if="imageValidation['whatsapp_file'].mediaType === 'image'"
                                        :src="whatsappFile"
                                        :alt="t('whatsapp_file_preview')"
                                        class="preview-media"
                                    />
                                    <video
                                        v-else-if="imageValidation['whatsapp_file'].mediaType === 'video'"
                                        :src="whatsappFile"
                                        class="preview-media"
                                        controls
                                        preload="metadata"
                                        style="max-width: 100%; max-height: 200px;"
                                    >
                                        {{ t('automations-form-actions-notification-whatsapp-video_not_supported') }}
                                    </video>
                                </div>
                            </div>
                        </div>

                        <div v-if="whatsappFileError" class="invalid-feedback">
                            {{ whatsappFileError }}
                        </div>
                    </section>
                </div>
            </template>
            <template v-if="sendMethod === 'sms'">
                <div class="input-group">
                    <textarea class="form-control" v-model="content"></textarea>
                    <char-counter :size="remainingChars"></char-counter>
                </div>
                <small><br/>
                    {{ t('automations-form-actions-notification-sms-accents_help') }}
                </small>
            </template>
            <template v-if="sendMethod === 'rcs'">
                <div class="field">
                    <label for="rcsType">{{ t('automations-form-actions-notification-rcs-type_label') }}</label>
                    <select id="rcsType" class="form-control" v-model="rcsType">
                        <option value="basic">{{ t('automations-form-actions-notification-rcs-type-basic') }}</option>
                        <option value="single">{{ t('automations-form-actions-notification-rcs-type-single') }}</option>
                        <option value="card">{{ t('automations-form-actions-notification-rcs-type-card') }}</option>
                        <option value="carousel">{{ t('automations-form-actions-notification-rcs-type-carousel') }}</option>
                        <option value="file">{{ t('automations-form-actions-notification-rcs-type-file') }}</option>
                    </select>
                </div>
                <template v-if="rcsType === 'basic' || rcsType === 'single'">
                    <div class="input-group">
                        <textarea class="form-control" v-model="content"></textarea>
                        <char-counter :size="remainingChars"></char-counter>
                    </div>
                    <small><br/>{{ t('automations-form-actions-notification-rcs-text_help') }}</small>
                </template>
                <template v-else-if="rcsType === 'card'">
                    <article class="rcs-editor-card is-standalone">
                        <header class="rcs-editor-card-head">
                            <strong>{{ rcsOptions.cardTitle || t('automations-form-actions-notification-rcs-card') }}</strong>
                        </header>
                        <div class="rcs-editor-media" :class="{ 'is-empty': !rcsPublicImage(rcsOptions.cardImage) || rcsEditorBrokenImages[rcsOptions.cardImage] }">
                            <img
                                v-if="rcsPublicImage(rcsOptions.cardImage) && !rcsEditorBrokenImages[rcsOptions.cardImage]"
                                :src="rcsPublicImage(rcsOptions.cardImage)"
                                alt=""
                                @error="markRcsEditorImageBroken(rcsOptions.cardImage)"
                            />
                            <i v-else class="fa fa-picture-o"></i>
                        </div>
                        <label>{{ t('automations-form-actions-notification-rcs-image') }}</label>
                        <input class="form-control" type="text" v-model="rcsOptions.cardImage" :placeholder="t('automations-form-actions-notification-rcs-image')" />
                        <label>{{ t('automations-form-actions-notification-rcs-title') }}</label>
                        <input class="form-control" type="text" v-model="rcsOptions.cardTitle" :placeholder="t('automations-form-actions-notification-rcs-title')" />
                        <label>{{ t('automations-form-actions-notification-rcs-message') }}</label>
                        <textarea class="form-control" rows="3" v-model="rcsOptions.cardMessage" :placeholder="t('automations-form-actions-notification-rcs-message')"></textarea>
                        <div class="rcs-editor-buttons">
                            <span class="rcs-editor-buttons-label">{{ t('automations-form-actions-notification-rcs-button') }}</span>
                            <div v-for="(button, index) in rcsOptions.buttons" :key="'rcs-btn-' + index" class="rcs-editor-button-row">
                                <input class="form-control" type="text" v-model="button.text" :placeholder="t('automations-form-actions-notification-rcs-button_text')" />
                                <input class="form-control" type="text" v-model="button.url" :placeholder="t('automations-form-actions-notification-rcs-button_url')" />
                                <button
                                    v-if="rcsOptions.buttons.length > 1"
                                    type="button"
                                    class="rcs-editor-icon-btn"
                                    :aria-label="t('delete')"
                                    @click="rcsOptions.buttons.splice(index, 1)"
                                >
                                    <Icon icon-class="ui-x-close" size="3" color="#6C737F" />
                                </button>
                            </div>
                            <button type="button" class="rcs-editor-text-btn" @click="rcsOptions.buttons.push({ text: '', url: '' })">
                                <Icon icon-class="ui-plus" size="2" color="#384250" />
                                {{ t('automations-form-actions-notification-rcs-add_button') }}
                            </button>
                        </div>
                    </article>
                    <small v-if="!rcsButtonsAreValid(rcsOptions.buttons)" class="text-danger d-block">{{ t('automations-form-actions-notification-rcs-button_text_required') }}</small>
                </template>
                <template v-else-if="rcsType === 'carousel'">
                    <div class="rcs-carousel-editor">
                        <article v-for="(card, cardIndex) in rcsOptions.cards" :key="'rcs-card-' + cardIndex" class="rcs-editor-card">
                            <header class="rcs-editor-card-head">
                                <strong>{{ card.cardTitle || (t('automations-form-actions-notification-rcs-card') + ' ' + (cardIndex + 1)) }}</strong>
                                <button
                                    v-if="rcsOptions.cards.length > 1"
                                    type="button"
                                    class="rcs-editor-icon-btn"
                                    :aria-label="t('delete')"
                                    @click="removeRcsCard(cardIndex)"
                                >
                                    <Icon icon-class="ui-trash-01" size="3" color="#6C737F" />
                                </button>
                            </header>
                            <div class="rcs-editor-media" :class="{ 'is-empty': !rcsPublicImage(card.cardImage) || rcsEditorBrokenImages[card.cardImage] }">
                                <img
                                    v-if="rcsPublicImage(card.cardImage) && !rcsEditorBrokenImages[card.cardImage]"
                                    :src="rcsPublicImage(card.cardImage)"
                                    alt=""
                                    @error="markRcsEditorImageBroken(card.cardImage)"
                                />
                                <i v-else class="fa fa-picture-o"></i>
                            </div>
                            <label>{{ t('automations-form-actions-notification-rcs-image') }}</label>
                            <input class="form-control" type="text" v-model="card.cardImage" :placeholder="t('automations-form-actions-notification-rcs-image')" />
                            <label>{{ t('automations-form-actions-notification-rcs-title') }}</label>
                            <input class="form-control" type="text" v-model="card.cardTitle" :placeholder="t('automations-form-actions-notification-rcs-title')" />
                            <label>{{ t('automations-form-actions-notification-rcs-message') }}</label>
                            <textarea class="form-control" rows="3" v-model="card.cardMessage" :placeholder="t('automations-form-actions-notification-rcs-message')"></textarea>
                            <div class="rcs-editor-buttons">
                                <span class="rcs-editor-buttons-label">{{ t('automations-form-actions-notification-rcs-button') }}</span>
                                <div v-for="(button, buttonIndex) in card.buttons" :key="'rcs-card-btn-' + cardIndex + '-' + buttonIndex" class="rcs-editor-button-row">
                                    <input class="form-control" type="text" v-model="button.text" :placeholder="t('automations-form-actions-notification-rcs-button_text')" />
                                    <input class="form-control" type="text" v-model="button.url" :placeholder="t('automations-form-actions-notification-rcs-button_url')" />
                                    <button
                                        v-if="card.buttons.length > 1"
                                        type="button"
                                        class="rcs-editor-icon-btn"
                                        :aria-label="t('delete')"
                                        @click="card.buttons.splice(buttonIndex, 1)"
                                    >
                                        <Icon icon-class="ui-x-close" size="3" color="#6C737F" />
                                    </button>
                                </div>
                                <button type="button" class="rcs-editor-text-btn" @click="card.buttons.push({ text: '', url: '' })">
                                    <Icon icon-class="ui-plus" size="2" color="#384250" />
                                    {{ t('automations-form-actions-notification-rcs-add_button') }}
                                </button>
                            </div>
                        </article>
                        <button type="button" class="rcs-editor-add" @click="addRcsCard">
                            <Icon icon-class="ui-plus" size="5" color="#384250" />
                            <span>{{ t('automations-form-actions-notification-rcs-add_card') }}</span>
                        </button>
                    </div>
                    <small v-if="rcsOptions.cards.some((card) => !rcsButtonsAreValid(card.buttons))" class="text-danger d-block">{{ t('automations-form-actions-notification-rcs-button_text_required') }}</small>
                </template>
                <template v-else-if="rcsType === 'file'">
                    <article class="rcs-editor-card is-standalone">
                        <header class="rcs-editor-card-head">
                            <strong>{{ rcsOptions.fileName || t('automations-form-actions-notification-rcs-type-file') }}</strong>
                        </header>
                        <div class="rcs-editor-file">
                            <span class="rcs-editor-file-icon"><i class="fa fa-file-text-o"></i></span>
                            <span class="rcs-editor-file-meta">
                                <span class="rcs-editor-file-name">{{ rcsOptions.fileName || t('automations-form-actions-notification-rcs-type-file') }}</span>
                                <span v-if="rcsFileKind(rcsOptions.fileName, rcsOptions.file)" class="rcs-editor-file-kind">{{ rcsFileKind(rcsOptions.fileName, rcsOptions.file) }}</span>
                            </span>
                        </div>
                        <label>{{ t('automations-form-actions-notification-rcs-file') }}</label>
                        <input class="form-control" type="text" v-model="rcsOptions.file" :placeholder="t('automations-form-actions-notification-rcs-file')" />
                        <label>{{ t('automations-form-actions-notification-rcs-file_name') }}</label>
                        <input class="form-control" type="text" v-model="rcsOptions.fileName" :placeholder="t('automations-form-actions-notification-rcs-file_name')" />
                    </article>
                </template>
            </template>
        </div>
        <input id="tokenToCopy" v-model="tokenToCopy" type="text"/>
    </div>
</template>

<style scoped lang="scss">
iframe {
    border: none;
    width: 100%;
    min-height: 500px;
}

.iframe-holder {
    position: relative;
    border: 1px solid #333;
}

.rcs-carousel-editor {
    display: flex;
    align-items: stretch;
    gap: 12px;
    overflow-x: auto;
    padding: 4px 2px 12px;
}

.rcs-editor-card {
    display: flex;
    flex-direction: column;
    flex: 0 0 280px;
    width: 280px;
    background: #fff;
    border: 1px solid #E5E7EB;
    border-radius: 12px;
    padding: 12px;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06);

    &.is-standalone {
        flex-basis: 320px;
        width: 320px;
    }

    label {
        margin: 10px 0 4px;
        font-size: 12px;
        font-weight: 600;
        color: #384250;
    }

    .form-control {
        width: 100%;
    }
}

.rcs-editor-card-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 10px;

    strong {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 14px;
        color: #111827;
    }
}

.rcs-editor-media {
    height: 132px;
    border-radius: 8px;
    overflow: hidden;
    background: #F2F4F7;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }

    &.is-empty {
        display: flex;
        align-items: center;
        justify-content: center;
        color: #98A2B3;
        font-size: 22px;
    }
}

.rcs-editor-file {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    border-radius: 8px;
    background: #F2F4F7;
}

.rcs-editor-file-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: #E8F0FE;
    color: #0B57D0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex: 0 0 40px;
}

.rcs-editor-file-meta {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.rcs-editor-file-name {
    font-size: 14px;
    font-weight: 600;
    color: #111827;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.rcs-editor-file-kind {
    font-size: 12px;
    color: #6C737F;
}

.rcs-editor-buttons {
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px solid #E5E7EB;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.rcs-editor-buttons-label {
    font-size: 12px;
    font-weight: 600;
    color: #384250;
}

.rcs-editor-button-row {
    display: flex;
    align-items: center;
    gap: 6px;

    .form-control {
        min-width: 0;
    }
}

.rcs-editor-icon-btn,
.rcs-editor-text-btn,
.rcs-editor-add {
    border: 0;
    background: transparent;
    padding: 0;
    cursor: pointer;
}

.rcs-editor-icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    flex: 0 0 28px;
    border-radius: 6px;

    &:hover {
        background: #F2F4F7;
    }
}

.rcs-editor-text-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    align-self: flex-start;
    color: #384250;
    font-size: 13px;
    font-weight: 600;
}

.rcs-editor-add {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    flex: 0 0 160px;
    width: 160px;
    min-height: 220px;
    border: 1.5px dashed #D0D5DD;
    border-radius: 12px;
    color: #384250;
    font-size: 13px;
    font-weight: 600;
    background: #F9FAFB;

    &:hover {
        border-color: #98A2B3;
        background: #F2F4F7;
    }
}

.invalid-feedback {
    display: block;
    width: 100%;
    margin-top: 0.25rem;
    font-size: 0.875em;
    color: #dc3545;
}

.wpp-cloud-components {
    display: flex;
    flex-direction: column;
    gap: 20px;

    input {
        display: inline-block;
        width: 400px;
        max-width: 80%;
    }

    .text-example {
        padding: 15px;
        border-radius: 10px;
        background: #f5f5f5;
        display: inline-block;
        width: 416px;
        max-width: 80%;
        font-size: 14px;
        white-space: pre-wrap;
        strong {
            font-weight: 500;
            font-size: 16px;
        }
    }

    .url-input-section {
        margin-top: 15px;
        padding: 15px;
        background: #f9f9f9;
        border-radius: 8px;
        border: 1px solid #e0e0e0;

        strong {
            display: block;
            margin-bottom: 8px;
            color: #333;
        }

        input {
            width: 100%;
            max-width: none;
        }

        .mt-2 {
            margin-top: 8px;
        }

        .mt-3 {
            margin-top: 12px;
        }
    }

    .loading-feedback {
        color: #007bff;
        font-size: 12px;
        margin-top: 5px;
        font-style: italic;
    }
}
.image-preview-container,
.media-preview-container {
    margin: 10px 0;

    strong {
        display: block;
        margin-bottom: 8px;
        color: #333;
    }
}

.image-preview,
.media-preview {
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    padding: 8px;
    background: #fafafa;
    max-width: 200px;
    max-height: 200px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
}

.preview-image,
.preview-media {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    border-radius: 4px;
}

.default-preview {
    opacity: 0.8;
}

.custom-preview {
    border: 2px solid #28a745;
}

.custom-image-preview,
.custom-media-preview {
    margin-top: 10px;
}

.text-success {
    color: #28a745 !important;
}

.text-info {
    color: #17a2b8 !important;
}

.text-danger {
    color: #dc3545 !important;
}

.whatsapp-sender-selection {
    .whatsapp-sender-option {
        margin-top: 10px;
    }

    .radio-label {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0;
    }

    select.form-control {
        max-width: 420px;
    }
}

.default-sender-alert {
    display: block !important;
    text-align: left !important;
    line-height: 1.4 !important;
    margin-top: 8px !important;
    margin-bottom: 0 !important;
    padding: 8px 12px !important;
    font-size: 13px !important;

    a.default-sender-link {
        display: inline !important;
        float: none !important;
        color: #533f03 !important;
        font-weight: 600 !important;
        text-decoration: underline !important;

        &:hover {
            color: #000 !important;
        }
    }
}
</style>

<script>
import keviApi from "../../../keviApi";
import EmailVisualEditor from "../../../components/visualEditor/email-visual-editor.vue";
import Icon from "../../../components/icon/icon.vue";

export default {
    components: {
        EmailVisualEditor,
        Icon,
    },
    props: {
        value: {},
        properties: {},
        actionId: String,
    },
    data: function () {
        return {
            title: this.t("automations-form-actions-notification-title"),
            description: this.t("automations-form-actions-notification-description"),
            content: "",
            subject: "",
            sendMethod: "",
            // New: editor persistence for backwards compatibility and migration
            // - new action (no id) defaults to 'kv'
            // - existing action without email_editor falls back to 'grapejs'
            email_editor: null, // 'kv' | 'grapejs' | null
            email_doc: null, // KV editor document (JSON)
            email_doc_draft: null, // draft used while editing (discard on cancel)
            selectNumber: "manually", //manually, by_custom_field, by_last_branch
            recipient_mode: "client", //client, custom
            recipient_address: "",
            timeout: "",
            notification_schedule: "from_place",
            sendWhatsappFile: false,
            whatsappFile: "",
            editingEmail: false,
            editingEmailCode: false,
            propsLength: {
                nome: 15,
                sobrenome: 20,
                email: 30,
                telefone: 15,
                pontos: 3,
                codigo: 6,
                descricao: 30,
                valido_ate: 5,
                valor: 8,
                url: 28,
                desconto: 8,
                pedido_minimo: 8,
            },
            tokenToCopy: null,
            customFields: [],
            senderCustomField: null,
            senders: [],
            my_senders: [],
            templates: {},
            places_senders_id: null,
            whatsappCloudOptions: {
                template_id: null,
                params: {
                    header_text: [],
                    header_handle: [],
                    body_text: [],
                    body_handle: [],
                    buttons: [],
                    document_filename: "",
                    location_latitude: "",
                    location_longitude: "",
                    location_name: "",
                    location_address: "",
                },
            },
            whatsappFileError: null,
            imageValidation: {}, // Para controlar validação das imagens
            isHydratingProperties: false,
            rcsEditorBrokenImages: {},
            rcsType: "basic",
            rcsOptions: {
                cardImage: "",
                cardTitle: "",
                cardMessage: "",
                buttons: [{ text: "", url: "" }],
                cards: [{ cardImage: "", cardTitle: "", cardMessage: "", buttons: [{ text: "", url: "" }] }],
                file: "",
                fileName: "",
            },
        };
    },
    mounted: async function () {
        this.loadFields();
        this.$emit("mounted", this);
        if (this.$module("sms")) {
            this.sendMethod = "sms";
        } else if (this.$module("rcs")) {
            this.sendMethod = "rcs";
        } else {
            this.sendMethod = "email";
        }
        await this.loadSenders();
        this.changed();
    },
    computed: {
        currentActionFormId() {
            // In this automations UI, `actionId` is the index in `automationForm.actions`,
            // not the persisted DB id of the action. A brand-new action won't have `form.id` yet.
            const idx = parseInt(this.actionId);
            const current = this.$store?.state?.automationForm?.actions?.[idx];
            return current?.form?.id ?? null;
        },
        recipientIsEmail() {
            return this.sendMethod === "email";
        },
        recipientClientLabel() {
            return this.recipientIsEmail
                ? this.t("automations-form-actions-notification-recipient_options-client_email")
                : this.t("automations-form-actions-notification-recipient_options-client_phone");
        },
        recipientCustomLabel() {
            return this.recipientIsEmail
                ? this.t("automations-form-actions-notification-recipient_options-custom_email")
                : this.t("automations-form-actions-notification-recipient_options-custom_phone");
        },
        recipientAddressLabel() {
            return this.recipientIsEmail
                ? this.t("automations-form-actions-notification-recipient_address_label_email")
                : this.t("automations-form-actions-notification-recipient_address_label_phone");
        },
        recipientAddressPlaceholder() {
            return this.recipientIsEmail
                ? this.t("automations-form-actions-notification-recipient_address_placeholder_email")
                : this.t("automations-form-actions-notification-recipient_address_placeholder_phone");
        },
        recipientAddressError() {
            if (this.recipient_mode !== "custom") return null;
            if (String(this.recipient_address || "").trim().length > 0) return null;
            return this.t("automations-form-actions-notification-recipient_address_required_error");
        },
        resolvedEmailEditor() {
            if (this.sendMethod !== "email") return null;
            if (this.email_editor === "kv" || this.email_editor === "grapejs") {
                return this.email_editor;
            }
            // Starting from scratch: no HTML yet -> prefer KV editor
            if (!String(this.content || "").trim()) return "kv";
            // Existing action with no stored preference: fallback for compatibility
            return "grapejs";
        },
        wppCloudSenders() {
            return this.my_senders.filter(
                (s) => s.channel === "whatsapp-cloud",
            );
        },
        activeTemplate() {
            if (
                !this.places_senders_id ||
                !this.whatsappCloudOptions.template_id
            ) {
                return null;
            }

            const template = this.templates[this.places_senders_id];
            if (!template) {
                return null;
            }
            return template.find(
                (t) => t.id === this.whatsappCloudOptions.template_id,
            ) ?? null;
        },
        wppCloudMediaHeaderFormats() {
            return ["IMAGE", "VIDEO", "GIF", "DOCUMENT"];
        },
        wppCloudHeaderComponent() {
            const t = this.activeTemplate;
            if (!t?.components) return null;
            return t.components.find((c) => c.type === "HEADER") ?? null;
        },
        wppCloudLocationParamsFilled() {
            const p = this.whatsappCloudOptions.params;
            const keys = [
                "location_latitude",
                "location_longitude",
                "location_name",
                "location_address",
            ];
            return keys.every(
                (k) =>
                    p[k] !== undefined &&
                    p[k] !== null &&
                    String(p[k]).trim() !== "",
            );
        },
        // eslint-disable-next-line vue/return-in-computed-property
        templateVariables() {
            const activeTemplate = this.activeTemplate;
            const components = [];
            for (const component of activeTemplate.components) {
                if (component.type === "BODY") {
                    components.push(component.text);
                } else if (component.type === "variable") {
                    components.push(`{{${component.variable}}}`);
                }
            }
        },
        userCustomFields() {
            if (this.customFields) {
                return this.customFields.filter((f) => f.type === "user");
            }
            return [];
        },
        maxContentLength: function () {
            if (this.sendMethod === "sms" || (this.sendMethod === "rcs" && (this.rcsType === "basic" || this.rcsType === "single"))) {
                let maxLength = this.sendMethod === "rcs" && this.rcsType === "single" ? 1000 : 160;
                let content = this.content;
                let hasToken = false;
                for (let t in this.tokens) {
                    let token = this.tokens[t];
                    let prop = token.split(".")[1].replace(/]/g, "");
                    let propLength = token.length;

                    if (Object.keys(this.propsLength).includes(prop)) {
                        propLength = this.propsLength[prop];
                    }

                    let searchToken = token
                        .replace(/\[/g, "\\[")
                        .replace(/\]/g, "\\]");
                    let ocurrences = (
                        content.match(new RegExp(searchToken, "g")) || []
                    ).length;
                    if (ocurrences > 0) {
                        hasToken = true;
                    }

                    let diff = (token.length - propLength) * ocurrences;
                    maxLength += diff;
                }
                if (!hasToken) {
                    maxLength = this.sendMethod === "rcs" && this.rcsType === "single" ? 1000 : 160;
                }
                return maxLength;
            }
            return null;
        },
        remainingChars: function () {
            return this.maxContentLength - this.content.length;
        },
        actions: function () {
            return this.$store.state.automationForm.actions;
        },
        trigger: function () {
            return this.$store.state.automationForm.trigger;
        },
        tokens: function () {
            return this.$store.getters["automationForm/tokens"](this.actionId, this.sendMethod);
        },
    },
    methods: {
        deepClone(obj) {
            if (!obj) return obj;
            try {
                return JSON.parse(JSON.stringify(obj));
            } catch (e) {
                return obj;
            }
        },
        openEmailEditor() {
            this.editingEmail = true;
            this.editingEmailCode = false;

            // Seed KV draft (so cancel doesn't mutate persisted email_doc)
            const editor = this.resolvedEmailEditor;
            this.email_editor = editor;
            if (editor === "kv") {
                this.email_doc_draft = this.deepClone(this.email_doc);
            }
        },
        cancelEmailEditor() {
            this.editingEmail = false;
            this.editingEmailCode = false;
            this.email_doc_draft = null;
        },
        onKvEmailSave(payload) {
            const html = payload?.html ?? "";
            const doc = payload?.doc ?? null;
            this.email_editor = "kv";
            this.email_doc = doc;
            this.content = html;
            this.cancelEmailEditor();
        },
        onGrapesEsc() {
            // Intentionally no-op: Esc should not close the outer modal while editing email.
        },
        statusTranslate(status) {
            if (status === "APPROVED") {
                return this.t("automations-form-actions-notification-wpp_cloud-statuses-approved");
            }
            if (status === "REJECTED") {
                return this.t("automations-form-actions-notification-wpp_cloud-statuses-rejected");
            }
            if (status === "PENDING") {
                return this.t("automations-form-actions-notification-wpp_cloud-statuses-pending");
            }
            return status;
        },
        wppCloudHeaderExamplePreviewLabel(format) {
            if (format === "VIDEO") {
                return this.t("automations-form-actions-notification-wpp_cloud-header_preview_video");
            }
            if (format === "DOCUMENT") {
                return this.t("automations-form-actions-notification-wpp_cloud-header_preview_document");
            }
            if (format === "GIF") {
                return this.t("automations-form-actions-notification-wpp_cloud-header_preview_gif");
            }
            return this.t("automations-form-actions-notification-wpp_cloud-image_preview");
        },
        wppCloudHeaderMediaUrlLabel(format, index) {
            if (format === "VIDEO") {
                return this.t("automations-form-actions-notification-wpp_cloud-header_media_url_video", [index]);
            }
            if (format === "DOCUMENT") {
                return this.t("automations-form-actions-notification-wpp_cloud-header_media_url_document", [index]);
            }
            if (format === "GIF") {
                return this.t("automations-form-actions-notification-wpp_cloud-header_media_url_gif", [index]);
            }
            return this.t("automations-form-actions-notification-wpp_cloud-image_url_label", [index]);
        },
        wppCloudCustomMediaTypeLabel(mediaType) {
            if (mediaType === "video") {
                return this.t("automations-form-actions-notification-wpp_cloud-media_types-video");
            }
            if (mediaType === "document") {
                return this.t("automations-form-actions-notification-wpp_cloud-media_types-document");
            }
            return this.t("automations-form-actions-notification-wpp_cloud-media_types-image");
        },
        getSenders(method) {
            return this.my_senders.filter((s) => s.channel === method);
        },
        async loadSenders() {
            let options = keviApi.getOptions("/senders", "GET");
            const response = await this.$http(options);
            this.senders = response.body.available_integrations;
            this.my_senders = response.body.my_senders;
            this.my_senders
                .filter((sender) => {
                    return sender.channel === "whatsapp-cloud";
                })
                .forEach((sender) => {
                    let optionsTemplates = keviApi.getOptions(
                        "senders/" + sender.id + "/templates",
                        "GET",
                    );
                    this.$http(optionsTemplates).then((response) => {
                        this.$set(this.templates, sender.id, response.body);
                    });
                });
        },
        setIframeContent() {
            const iframe = this.$refs.iframe;
            if (!iframe) return;

            const doc = iframe.contentDocument || iframe.contentWindow.document;

            doc.open();
            doc.write(this.content);
            doc.close();
        },
        loadFields() {
            const options = keviApi.getOptions("client-fields", "GET");
            return this.$http(options).then((response) => {
                this.customFields = response.body;
            });
        },
        areTemplateFieldsFilled() {
            if (this.sendMethod !== "whatsapp-cloud" || !this.activeTemplate) {
                return true;
            }

            const params = this.whatsappCloudOptions.params;

            const isFilled = (v) => v !== undefined && v !== null && String(v).trim() !== "";

            const checkArray = (expected, provided) =>
                expected.every((_, i) => isFilled(provided?.[i]));

            const validators = {
                HEADER: (component, example) => {
                    if (component.format === "LOCATION") {
                        const locKeys = [
                            "location_latitude",
                            "location_longitude",
                            "location_name",
                            "location_address",
                        ];
                        return locKeys.every((k) => isFilled(params[k]));
                    }
                    if (example.header_text?.length) {
                        if (!checkArray(example.header_text, params.header_text)) return false;
                    }
                    if (example.header_handle?.length) {
                        if (!checkArray(example.header_handle, params.header_handle)) return false;
                    }
                    return true;
                },
                BODY: (component, example) => {
                    if (example.body_text?.[0]?.length) {
                        if (!checkArray(example.body_text[0], params.body_text)) return false;
                    }
                    if (example.body_handle?.length) {
                        for (const h of example.body_handle) {
                            const v = this.getBodyHandleValue(h.param_name);
                            if (!isFilled(v)) return false;
                        }
                    }
                    return true;
                },
                BUTTONS: (component) => {
                    if (!component.buttons) return true;
                    return component.buttons.every((btn, i) =>
                        (btn.type === "URL" || btn.type === "COPY_CODE") && btn.example
                            ? isFilled(params.buttons?.[i])
                            : true
                    );
                }
            };

            for (const c of this.activeTemplate.components) {
                const example = c.example;
                if (
                    !example &&
                    !(c.type === "HEADER" && c.format === "LOCATION")
                ) {
                    continue;
                }

                const validate = validators[c.type];
                if (validate && !validate(c, example || {})) return false;
            }

            return true;
        },
        isValid: function () {
            let contentValid = true;
            if (this.recipientAddressError) {
                return false;
            }
            if (this.sendMethod === "whatsapp-cloud") {
                if (!this.areTemplateFieldsFilled()) {
                    return false;
                }
                return true;
            }
            const rcsRich = this.sendMethod === "rcs" && this.rcsType !== "basic" && this.rcsType !== "single";
            if (
                (!rcsRich && this.content.trim().length === 0) ||
                (this.sendMethod === "whatsapp-cloud" &&
                    !this.whatsappCloudOptions.template_id)
            ) {
                contentValid = false;
            }
            if (this.sendMethod === "sms" && this.remainingChars < 0) {
                contentValid = false;
            }
            if (this.sendMethod === "rcs" && !this.isRcsContentValid()) {
                contentValid = false;
            }
            if (this.sendMethod === "rcs" && !this.places_senders_id && !this.hasDefaultSender("rcs")) {
                contentValid = false;
            }
            if (
                this.sendMethod === "email" &&
                (!this.subject || this.subject.trim().length < 4)
            ) {
                contentValid = false;
            }
            if (this.sendWhatsappFile && this.whatsappFileError) {
                contentValid = false;
            }
            return contentValid && this.sendMethod.length > 0;
        },
        changed: function () {
            this.$emit("propertiesUpdated", {
                content: this.content,
                subject: this.subject,
                whatsappFile: this.whatsappFile,
                selectNumber: this.selectNumber,
                senderCustomField: this.senderCustomField,
                recipient_mode: this.recipient_mode,
                recipient_address:
                    this.recipient_mode === "custom" ? this.recipient_address : "",
                sendMethod: this.sendMethod,
                rcsType: this.rcsType,
                rcsOptions: this.rcsOptions,
                timeout: this.timeout ? this.timeout : 0,
                notification_schedule: this.notification_schedule,
                places_senders_id: this.places_senders_id,
                whatsappCloudOptions: this.whatsappCloudOptions,
                // New: always persist editor preference (migration + compatibility)
                email_editor: this.resolvedEmailEditor,
                // KV editor doc (safe to persist even if current editor is grapesjs; keeps future edits possible)
                email_doc: this.email_doc,
            });
        },
        addRcsCard() {
            this.rcsOptions.cards.push({
                cardImage: "",
                cardTitle: "",
                cardMessage: "",
                buttons: [{ text: "", url: "" }],
            });
            this.$nextTick(() => {
                const row = this.$el && this.$el.querySelector(".rcs-carousel-editor");
                if (row) {
                    row.scrollLeft = row.scrollWidth;
                }
            });
        },
        removeRcsCard(index) {
            if (this.rcsOptions.cards.length > 1) {
                this.rcsOptions.cards.splice(index, 1);
            }
        },
        rcsFileKind(fileName, fileUrl) {
            const fromName = String(fileName || "").match(/\.([a-z0-9]{1,8})$/i);
            if (fromName) {
                return fromName[1].toUpperCase();
            }
            const path = String(fileUrl || "").split("?")[0];
            const fromUrl = path.match(/\.([a-z0-9]{1,8})$/i);
            return fromUrl ? fromUrl[1].toUpperCase() : "";
        },
        rcsPublicImage(value) {
            const url = String(value || "").trim();
            return /^https?:\/\//i.test(url) ? url : "";
        },
        markRcsEditorImageBroken(src) {
            if (src) {
                this.$set(this.rcsEditorBrokenImages, src, true);
            }
        },
        rcsButtonsAreValid(buttons) {
            return (buttons || []).every((button) => {
                const text = String((button && button.text) || "").trim();
                const url = String((button && button.url) || "").trim();
                if (!text && !url) {
                    return true;
                }
                return text.length > 0;
            });
        },
        isRcsContentValid() {
            if (this.rcsType === "basic" || this.rcsType === "single") {
                return this.content.trim().length > 0 && this.remainingChars >= 0;
            }
            if (this.rcsType === "card") {
                return Boolean(
                    String(this.rcsOptions.cardImage || "").trim() &&
                    String(this.rcsOptions.cardTitle || "").trim() &&
                    String(this.rcsOptions.cardMessage || "").trim() &&
                    this.rcsButtonsAreValid(this.rcsOptions.buttons)
                );
            }
            if (this.rcsType === "carousel") {
                const cards = this.rcsOptions.cards || [];
                if (!cards.length) {
                    return false;
                }
                return cards.every((card) =>
                    String(card.cardImage || "").trim() &&
                    String(card.cardTitle || "").trim() &&
                    String(card.cardMessage || "").trim() &&
                    this.rcsButtonsAreValid(card.buttons)
                );
            }
            if (this.rcsType === "file") {
                return Boolean(
                    String(this.rcsOptions.file || "").trim() &&
                    String(this.rcsOptions.fileName || "").trim()
                );
            }
            return false;
        },
        copyToken(token) {
            this.tokenToCopy = token;
            this.$nextTick(() => {
                let tokenToCopyInput = document.querySelector("#tokenToCopy");
                tokenToCopyInput.select();
                if (document.execCommand("copy")) {
                    this.$store.dispatch("notification/success", {
                        text: this.t("automations-form-actions-notification-common-token_copied"),
                        duration: 5000,
                    });
                }
            });
        },
        validateWhatsappFile: function() {
            this.whatsappFileError = null;

            if (this.whatsappFile && this.whatsappFile.length > 240) {
                this.whatsappFileError = this.t("automations-form-actions-notification-whatsapp-file_url_limit_error");
                return;
            }
        },
        validateMediaUrl(url, key, headerFormat = null) {
            this.$set(this.imageValidation, key, { isLoading: true, isValid: false, error: null, isSmartTag: false, mediaType: null });

            if (!url || !url.trim()) {
                this.$set(this.imageValidation, key, { isLoading: false, isValid: false, error: this.t("automations-form-actions-notification-wpp_cloud-url_empty_error"), isSmartTag: false, mediaType: null });
                return;
            }

            const smartTagPattern = /^\[\[[^\]]+\]\]$/;
            if (smartTagPattern.test(url.trim())) {
                const isValidTag = this.tokens.includes(url.trim());

                if (isValidTag) {
                    this.$set(this.imageValidation, key, {
                        isLoading: false,
                        isValid: true,
                        error: this.t("automations-form-actions-notification-wpp_cloud-smart_tag_valid_success"),
                        isSmartTag: true,
                        mediaType: headerFormat === "DOCUMENT" ? "document" : null
                    });
                } else {
                    this.$set(this.imageValidation, key, {
                        isLoading: false,
                        isValid: false,
                        error: this.t("automations-form-actions-notification-wpp_cloud-smart_tag_not_found_error"),
                        isSmartTag: false,
                        mediaType: null
                    });
                }
                return;
            }

            const trimmed = url.trim();
            const urlLower = url.toLowerCase();

            if (headerFormat === "DOCUMENT") {
                const docExtensions = [".pdf", ".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx", ".txt", ".csv", ".odt", ".ods", ".rtf", ".zip"];
                const pathPart = trimmed.split("?")[0].split("#")[0];
                const lowerPath = pathPart.toLowerCase();
                const hasDocExt = docExtensions.some((ext) => lowerPath.endsWith(ext));
                const isHttp = /^https?:\/\//i.test(trimmed);
                if (hasDocExt || isHttp) {
                    this.$set(this.imageValidation, key, {
                        isLoading: false,
                        isValid: true,
                        error: null,
                        isSmartTag: false,
                        mediaType: "document",
                    });
                } else {
                    this.$set(this.imageValidation, key, {
                        isLoading: false,
                        isValid: false,
                        error: this.t("automations-form-actions-notification-wpp_cloud-invalid_document_url_error"),
                        isSmartTag: false,
                        mediaType: "document",
                    });
                }
                return;
            }

            const runVideoProbe = () => {
                const videoElement = document.createElement("video");

                const timeoutId = setTimeout(() => {
                    this.$set(this.imageValidation, key, {
                        isLoading: false,
                        isValid: true,
                        error: this.t("automations-form-actions-notification-wpp_cloud-timeout_video_error"),
                        isSmartTag: false,
                        mediaType: "video"
                    });
                }, 5000);

                videoElement.onloadedmetadata = () => {
                    clearTimeout(timeoutId);
                    this.$set(this.imageValidation, key, {
                        isLoading: false,
                        isValid: true,
                        error: null,
                        isSmartTag: false,
                        mediaType: "video"
                    });
                };

                videoElement.onerror = () => {
                    clearTimeout(timeoutId);
                    this.$set(this.imageValidation, key, {
                        isLoading: false,
                        isValid: false,
                        error: this.t("automations-form-actions-notification-wpp_cloud-invalid_video_error"),
                        isSmartTag: false,
                        mediaType: "video"
                    });
                };

                videoElement.preload = "metadata";
                videoElement.src = url;
            };

            if (headerFormat === "VIDEO") {
                runVideoProbe();
                return;
            }

            const videoExtensions = [".mp4", ".avi", ".mov", ".wmv", ".flv", ".webm", ".mkv", ".m4v"];
            const isVideo = videoExtensions.some(ext => urlLower.includes(ext));

            if (isVideo) {
                runVideoProbe();
            } else {
                const img = new Image();

                const timeoutId = setTimeout(() => {
                    this.$set(this.imageValidation, key, {
                        isLoading: false,
                        isValid: true,
                        error: this.t("automations-form-actions-notification-wpp_cloud-timeout_url_error"),
                        isSmartTag: false,
                        mediaType: "image"
                    });
                }, 5000);

                img.onload = () => {
                    clearTimeout(timeoutId);
                    this.$set(this.imageValidation, key, {
                        isLoading: false,
                        isValid: true,
                        error: null,
                        isSmartTag: false,
                        mediaType: "image"
                    });
                };

                img.onerror = () => {
                    clearTimeout(timeoutId);
                    this.$set(this.imageValidation, key, {
                        isLoading: false,
                        isValid: false,
                        error: this.t("automations-form-actions-notification-wpp_cloud-invalid_image_error"),
                        isSmartTag: false,
                        mediaType: "image"
                    });
                };

                img.src = url;
            }
        },
        getBodyHandleValue(paramName) {
            const bodyHandleArray = this.whatsappCloudOptions.params.body_handle;
            const foundParam = bodyHandleArray.find(param => param.param_name === paramName);
            return foundParam ? foundParam.value : "";
        },
        updateBodyHandle(paramName, value) {
            let bodyHandleArray = [...this.whatsappCloudOptions.params.body_handle];
            const existingIndex = bodyHandleArray.findIndex(param => param.param_name === paramName);

            if (existingIndex !== -1) {
                bodyHandleArray[existingIndex] = {
                    param_name: paramName,
                    value: value
                };
            } else {
                bodyHandleArray.push({
                    param_name: paramName,
                    value: value
                });
            }

            this.$set(this.whatsappCloudOptions.params, "body_handle", bodyHandleArray);
            this.changed();
        },

        connectionsUrl(method = this.sendMethod) {
            const channel = method || "whatsapp";
            return `/#/connections?tab=${channel}`;
        },
        getDefaultSender(method = this.sendMethod) {
            if (!method) return null;
            return (
                this.getSenders(method).find(
                    (s) =>
                        String(s.default_sender) === "1" ||
                        s.default_sender === 1 ||
                        s.default_sender === true
                ) || null
            );
        },
        hasDefaultSender(method = this.sendMethod) {
            return !!this.getDefaultSender(method);
        },
        defaultSenderOptionLabel(method = this.sendMethod) {
            const defaultSender = this.getDefaultSender(method);
            const baseLabel = this.t(
                "automations-form-actions-notification-sender_default"
            );
            if (defaultSender) {
                return `${baseLabel} (${defaultSender.alias || defaultSender.sender_name || ""})`;
            }
            const noDefault =
                this.t(
                    "automations-form-actions-notification-no_default_sender"
                ) || "Nothing set";
            return `${baseLabel} (${noDefault})`;
        },
    },
    watch: {
        "whatsappCloudOptions.template_id": function (newVal, oldVal) {
            if (newVal && oldVal) {
                this.whatsappCloudOptions.params = {
                    header_text: [],
                    header_handle: [],
                    body_text: [],
                    body_handle: [],
                    buttons: [],
                    document_filename: "",
                    location_latitude: "",
                    location_longitude: "",
                    location_name: "",
                    location_address: "",
                };
            }
        },
        properties: function () {
            this.isHydratingProperties = true;
            this.content = this.properties.content
                ? this.properties.content
                : "";
            this.subject = this.properties.subject
                ? this.properties.subject
                : "";
            const defaultMethod = this.$module("sms") ? "sms" : (this.$module("rcs") ? "rcs" : "email");
            this.sendMethod = this.properties.sendMethod
                ? this.properties.sendMethod
                : defaultMethod;
            this.timeout = this.properties.timeout
                ? this.properties.timeout
                : "";
            this.notification_schedule =
                this.properties.notification_schedule !== undefined
                    ? this.properties.notification_schedule
                    : "from_place";
            this.whatsappFile =
                this.properties.whatsappFile !== undefined
                    ? this.properties.whatsappFile
                    : "";
            this.sendWhatsappFile = !!this.whatsappFile;
            this.selectNumber =
                this.properties.selectNumber !== undefined
                    ? this.properties.selectNumber
                    : "manually";
            this.senderCustomField =
                this.properties.senderCustomField !== undefined
                    ? this.properties.senderCustomField
                    : "";
            this.recipient_mode =
                this.properties.recipient_mode === "custom" ? "custom" : "client";
            this.recipient_address =
                this.properties.recipient_address !== undefined &&
                this.properties.recipient_address !== null
                    ? this.properties.recipient_address
                    : "";
            this.places_senders_id =
                this.properties.places_senders_id !== undefined && this.properties.places_senders_id !== null
                    ? String(this.properties.places_senders_id)
                    : null;
            this.whatsappCloudOptions =
                this.properties.whatsappCloudOptions !== undefined
                    ? this.properties.whatsappCloudOptions
                    : {
                        template_id: null,
                        params: {
                            header_text: [],
                            header_handle: [],
                            body_text: [],
                            body_handle: [],
                            buttons: [],
                            document_filename: "",
                            location_latitude: "",
                            location_longitude: "",
                            location_name: "",
                            location_address: "",
                        },
                    };

            // New: restore email editor preference + KV doc (when present)
            this.email_editor =
                this.properties.email_editor !== undefined
                    ? this.properties.email_editor
                    : null;
            this.email_doc =
                this.properties.email_doc !== undefined
                    ? this.properties.email_doc
                    : null;
            // Ensure we don't keep stale drafts when switching actions/props
            this.email_doc_draft = null;
            this.rcsType = this.properties.rcsType || "basic";
            const incomingRcs = this.properties.rcsOptions && typeof this.properties.rcsOptions === "object"
                ? this.properties.rcsOptions
                : {};
            this.rcsOptions = {
                cardImage: incomingRcs.cardImage || "",
                cardTitle: incomingRcs.cardTitle || "",
                cardMessage: incomingRcs.cardMessage || "",
                buttons: Array.isArray(incomingRcs.buttons) && incomingRcs.buttons.length
                    ? incomingRcs.buttons
                    : [{ text: "", url: "" }],
                cards: Array.isArray(incomingRcs.cards) && incomingRcs.cards.length
                    ? incomingRcs.cards.map((card) => ({
                        cardImage: card.cardImage || "",
                        cardTitle: card.cardTitle || "",
                        cardMessage: card.cardMessage || "",
                        buttons: Array.isArray(card.buttons) && card.buttons.length
                            ? card.buttons
                            : [{ text: "", url: "" }],
                    }))
                    : [{ cardImage: "", cardTitle: "", cardMessage: "", buttons: [{ text: "", url: "" }] }],
                file: incomingRcs.file || "",
                fileName: incomingRcs.fileName || "",
            };

            if (this.whatsappCloudOptions && this.whatsappCloudOptions.params) {
                // Garantir que header_handle existe quando vem do banco para itens antes desta atualização
                if (!this.whatsappCloudOptions.params.header_handle) {
                    this.whatsappCloudOptions.params.header_handle = [];
                }

                // Garantir que body_handle existe quando vem do banco para itens antes desta atualização
                if (!this.whatsappCloudOptions.params.body_handle) {
                    this.whatsappCloudOptions.params.body_handle = [];
                }

                const p = this.whatsappCloudOptions.params;
                if (p.document_filename === undefined) {
                    this.$set(p, "document_filename", "");
                }
                for (const locKey of [
                    "location_latitude",
                    "location_longitude",
                    "location_name",
                    "location_address",
                ]) {
                    if (p[locKey] === undefined) {
                        this.$set(p, locKey, "");
                    }
                }
            }
            this.$nextTick(() => {
                this.isHydratingProperties = false;
            });
        },
        whatsappCloudOptions: {
            handler: function () {
                this.changed();
            },
            deep: true,
        },
        "whatsappCloudOptions.params.header_handle": {
            handler: function (newVal) {
                if (newVal && Array.isArray(newVal)) {
                    const fmt = this.wppCloudHeaderComponent?.format ?? null;
                    newVal.forEach((url, index) => {
                        if (url) {
                            this.validateMediaUrl(url, `header_handle_${index}`, fmt);
                        }
                    });
                }
            },
            deep: true,
        },
        "whatsappCloudOptions.params.body_handle": {
            handler: function () {
                this.changed();
            },
            deep: true,
        },
        places_senders_id: function () {
            this.changed();
        },
        content: function () {
            this.changed();
            this.$nextTick(() => {
                this.setIframeContent();
            });
        },
        rcsType: function () {
            if (this.isHydratingProperties) {
                return;
            }
            this.changed();
        },
        rcsOptions: {
            handler: function () {
                if (this.isHydratingProperties) {
                    return;
                }
                this.changed();
            },
            deep: true,
        },
        sendMethod: function (val, oldVal) {
            this.changed();
            if (val !== "whatsapp") {
                this.selectNumber = "manually";
                this.senderCustomField = null;
            } else if (!this.selectNumber) {
                this.selectNumber = "manually";
            }
            this.$nextTick(() => {
                if (!this.isHydratingProperties && oldVal && val && oldVal !== val) {
                    this.places_senders_id = null;
                    // A phone destination is meaningless for email and vice-versa.
                    if ((oldVal === "email") !== (val === "email")) {
                        this.recipient_address = "";
                    }
                }
                this.setIframeContent();
            });
        },
        timeout: function () {
            this.changed();
        },
        subject: function () {
            this.changed();
        },
        notification_schedule: function () {
            this.changed();
        },
        whatsappFile: function () {
            this.validateWhatsappFile();
            if (this.whatsappFile) {
                this.validateMediaUrl(this.whatsappFile, "whatsapp_file");
            }
            this.changed();
        },
        senderCustomField: function () {
            this.changed();
        },
        recipient_mode: function (val) {
            if (val !== "custom" && !this.isHydratingProperties) {
                this.recipient_address = "";
            }
            this.changed();
        },
        recipient_address: function () {
            this.changed();
        },
        selectNumber: function () {
            if (this.sendMethod === "whatsapp" && this.selectNumber !== "manually") {
                this.places_senders_id = null;
            }
            this.changed();
        },
        sendWhatsappFile: function (val) {
            if (!val) {
                this.whatsappFile = "";
                this.whatsappFileError = null;
                this.$set(this.imageValidation, "whatsapp_file", { isLoading: false, isValid: false, error: null, isSmartTag: false });
            }
        },
    },
};
</script>
